import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, Button, ListItem, ListItemText, List, Tooltip, IconButton, FormControl, Select, MenuItem } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useDeleteLeadsMutation, useGetAllUniqueQueryNamesQuery, useDeleteLeadsByQueryHashMutation } from 'services/user-service';
import { toast } from 'react-hot-toast';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const LeadSetting = ({ open, onClose, userId, workspaceIds, showQueryDropdown }) => {
  const [deleteLeads] = useDeleteLeadsMutation();
  const [deleteLeadsByQueryHash] = useDeleteLeadsByQueryHashMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState("");
  const [selectedQuery, setSelectedQuery] = useState("");

  // Only fetch queries when a workspace is selected
  const { data: queryNames = [], isLoading: loadingQueries } = useGetAllUniqueQueryNamesQuery(selectedWorkspace, {
    skip: !selectedWorkspace,
  });

  const handleWorkspaceChange = (e) => {
    setSelectedWorkspace(e.target.value);
    setSelectedQuery(""); // Reset query when workspace changes
  };

  const handleDelete = async () => {
    try {
      if (showQueryDropdown && selectedQuery) {
        // Find the selected queryHash from queryNames
        const selectedQueryObj = queryNames.find(q => q.name === selectedQuery);
        if (!selectedQueryObj?.queryHash) {
          toast.error("No query hash found for selected query");
          return;
        }
        await deleteLeadsByQueryHash(selectedQueryObj.queryHash).unwrap();
        toast.success("Deleted leads by query successfully");
      } else {
        // Your existing deleteLeads logic for non-query mode
        const response = await deleteLeads({
          userId,
          workspaceId: selectedWorkspace,
        }).unwrap();
        toast.success(`${response?.message}`);
      }
      setConfirmOpen(false);
      onClose();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  useEffect(() => {
    setSelectedWorkspace("");
    setSelectedQuery("");
  }, [open, userId, workspaceIds]);

  const listItems = showQueryDropdown
    ? [
        { label: 'Company batch CSV data', tooltip: 'All company batch CSV files uploaded for company operations.' },
        { label: 'Saved leads', tooltip: 'Leads you have manually saved during research or import.' },
      ]
    : [
        { label: 'All batch CSV data', tooltip: 'Includes all previously uploaded CSV files for batch operations.' },
        { label: 'Saved leads', tooltip: 'Leads you have manually saved during research or import.' },
        { label: 'Campaign leads', tooltip: 'Leads associated with any running or past campaigns.' },
        { label: 'Lists (excluding master lists)', tooltip: 'Custom lists you’ve created, excluding system default ones.' },
        { label: 'Company batch CSV data', tooltip: 'All company batch CSV files uploaded for company operations.' },
        { label: 'Leads lists', tooltip: 'All leads lists created in your workspace, except master lists.' },
      ];

  return (
    <>
      <Modal open={open} onClose={onClose} aria-labelledby="remove-tabs-title" aria-describedby="remove-tabs-description">
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 420,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}>
          <Typography id="remove-tabs-title" variant="h5" fontWeight={600} gutterBottom>
            Remove All Saved Tabs
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <WarningAmberRoundedIcon color="error" sx={{ mr: 1 }} />
            <Typography variant="h6" color="error">
              Warning!
            </Typography>
          </Box>
          <Typography id="remove-tabs-description" variant="body1" sx={{ mb: 2 }}>
            This action will permanently delete:
          </Typography>
          {/* Workspace Dropdown */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <Select
              labelId="workspace-select-label"
              id="workspace-select"
              value={selectedWorkspace}
              onChange={handleWorkspaceChange}
              size="small"
              displayEmpty
              renderValue={(selected) =>
                selected
                  ? workspaceIds.find((ws) => ws.id === selected)?.label
                  : "Workspaces"
              }
            >
              {workspaceIds.map((ws) => (
                <MenuItem key={ws.id} value={ws.id}>
                  {ws.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Query Dropdown (only if showQueryDropdown and workspace selected) */}
          {showQueryDropdown && selectedWorkspace && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <Select
                labelId="query-select-label"
                id="query-select"
                value={selectedQuery}
                onChange={(e) => setSelectedQuery(e.target.value)}
                size="small"
                displayEmpty
                renderValue={(selected) =>
                  selected
                    ? selected
                    : "Query"
                }
              >
                {loadingQueries ? (
                  <MenuItem disabled>Loading...</MenuItem>
                ) : (
                  queryNames.map((q, idx) => (
                    <MenuItem key={idx} value={q.name}>
                      {q.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          )}
          <List dense sx={{ pl: 2 }}>
            {listItems.map(({ label, tooltip }) => (
              <ListItem key={label} disableGutters>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ListItemText primary={label} />
                  <Tooltip title={tooltip} arrow>
                    <IconButton size="small">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
            ))}
          </List>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
            <Button onClick={onClose} variant="outlined" sx={{ mr: 2 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => setConfirmOpen(true)}
              disabled={
                !selectedWorkspace ||
                (showQueryDropdown && !selectedQuery) // Disable if query dropdown is shown and no query is selected
              }
            >
              Yes, Delete All
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="confirmation-modal-title"
        aria-describedby="confirmation-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 300,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}>
          <Typography id="confirmation-modal-title" variant="h6" component="h2">
            Are you sure?
          </Typography>
          <Typography id="confirmation-modal-description" sx={{ mt: 2 }}>
            Do you want to remove all saved leads of the user?
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={() => setConfirmOpen(false)} sx={{ mr: 2 }}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleDelete} >
              Yes
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default React.memo(LeadSetting);